import { motion } from "framer-motion";
import { ArrowRight, Apple, Monitor, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import Motto from "@/components/site/Motto";
import { useTheme } from "@/lib/theme";
import { GOOGLE_PLAY_URL } from "@/lib/appLinks";

const HERO_DAY = "/assets/site/hero.webp";
const HERO_NIGHT = "/assets/site/hero-night.webp";

function CtaGroup({ solidGlass = false }) {
  return (
    <>
      <div className="flex flex-wrap items-center justify-center gap-3 w-full max-w-sm md:max-w-none">
        <a
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-amber flex-1 md:flex-none justify-center"
          data-testid={solidGlass ? "hero-cta-primary" : "hero-cta-primary-desktop"}
        >
          <Smartphone className="w-4 h-4" />
          Get it on Google Play
        </a>
        <Link
          to="/pricing"
          className="btn btn-light flex-1 md:flex-none justify-center"
          data-testid={solidGlass ? "hero-cta-secondary" : "hero-cta-secondary-desktop"}
        >
          See Pricing
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <a
          href="https://apps.apple.com/my/app/myadventure-passport/id6803114683"
          target="_blank"
          rel="noopener noreferrer"
          className={`btn ${solidGlass ? "btn-glass-solid" : "btn-glass"}`}
          data-testid={solidGlass ? "badge-ios" : "badge-ios-desktop"}
        >
          <Apple className="w-4 h-4" /> iOS
        </a>
        <a
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`btn ${solidGlass ? "btn-glass-solid" : "btn-glass"}`}
          data-testid={solidGlass ? "badge-android" : "badge-android-desktop"}
        >
          <Smartphone className="w-4 h-4" /> Android
        </a>
        <span
          className={`btn ${solidGlass ? "btn-glass-solid" : "btn-glass"}`}
          data-testid={solidGlass ? "badge-web" : "badge-web-desktop"}
        >
          <Monitor className="w-4 h-4" /> Web
        </span>
      </div>
    </>
  );
}

export default function Hero() {
  const { mode } = useTheme();
  const img = mode === "night" ? HERO_NIGHT : HERO_DAY;

  return (
    <section
      data-testid="hero-section"
      className="relative w-full pt-[92px] md:pt-[104px]"
    >
      {/* Image band */}
      <div className="relative w-full overflow-hidden">
        <div
          className="relative w-full aspect-[4/5] sm:aspect-[3/2] md:aspect-[2/1]"
          style={{ maxHeight: 820 }}
        >
          <motion.img
            key={img}
            src={img}
            alt="Campsite at dawn with mountains, lake, and tent"
            initial={{ scale: 1.08, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.65) 100%)",
            }}
          />

          {/* Motto + tagline (mobile: centered vertically in the taller image;
              desktop: sits in the upper portion above the CTA overlay). */}
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center md:justify-start md:pt-[14%] px-5 sm:px-6 pb-40 md:pb-0 text-center">
            <Motto />
            <p
              data-testid="hero-tagline"
              className="mt-4 sm:mt-6 md:mt-8 max-w-[92%] md:max-w-2xl text-white text-sm sm:text-base md:text-lg leading-relaxed drop-shadow-lg"
              style={{ opacity: 1 }}
            >
              Your digital passport for discovering campsites, collecting stamps, and remembering every adventure.
            </p>
          </div>

          {/* Desktop CTA — overlays bottom of the hero image */}
          <div className="hidden md:flex absolute left-0 right-0 bottom-8 z-20 flex-col items-center gap-4 px-6">
            <CtaGroup solidGlass={false} />
          </div>
        </div>
      </div>

      {/* Mobile CTA — lives BELOW the image on its own strip so nothing overlaps. */}
      <div
        className="md:hidden flex flex-col items-center gap-3 px-5 py-6"
        style={{ background: "var(--surface)" }}
      >
        <CtaGroup solidGlass={true} />
      </div>
    </section>
  );
}
